import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Alert, 
  Grid, 
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { integrationService } from '../services/api';

const IntegrationsPage = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();

  useEffect(() => {
    fetchIntegrations();
    
    // Check for success/error query params
    const params = new URLSearchParams(location.search);
    if (params.get('connected') === 'true') {
      setError({ type: 'success', message: 'Integration connected successfully!' });
    } else if (params.get('error') === 'true') {
      setError({ type: 'error', message: 'Failed to connect integration. Please try again.' });
    }
  }, [location]);

  const fetchIntegrations = async () => {
    try {
      const response = await integrationService.getIntegrations();
      setIntegrations(response);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching integrations:', err);
      setError('Failed to fetch integrations.');
      setLoading(false);
    }
  };

  const handleOutlookConnect = async () => {
    try {
      const response = await integrationService.getOutlookAuthUrl();
      window.location.href = response.authUrl;
    } catch (err) {
      console.error('Error initiating Outlook authentication:', err);
      setError('Failed to initiate Outlook authentication.');
    }
  };

  const handleGoogleConnect = async () => {
    try {
      const response = await integrationService.getGoogleAuthUrl();
      window.location.href = response.authUrl;
    } catch (err) {
      setError({ type: 'error', message: 'Failed to initiate Google authentication' });
    }
  };
  
  const handleGhlConnect = async () => {
    try {
      const response = await integrationService.getGhlAuthUrl();
      window.location.href = response.authUrl;
    } catch (err) {
      console.error('Error initiating GHL authentication:', err);
      setError({ type: 'error', message: 'Failed to initiate GHL authentication' });
    }
  };

  const handleRevoke = async (integrationId) => {
    if (window.confirm('Are you sure you want to revoke this integration?')) {
      try {
        await integrationService.revokeIntegration(integrationId);
        fetchIntegrations();
      } catch (err) {
        setError({ type: 'error', message: 'Failed to revoke integration' });
      }
    }
  };

  const getIntegrationStatus = (provider) => {
    const integration = integrations.find(i => i.provider === provider && i.status === 'active');
    return integration ? 'connected' : 'disconnected';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Integrations
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Connect your favorite tools and services to enhance your Call Ninja experience.
      </Typography>

      {error && (
        <Alert 
          severity={error.type || 'error'} 
          onClose={() => setError('')} 
          sx={{ mb: 3 }}
        >
          {error.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Microsoft Outlook */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Microsoft Outlook</Typography>
                <Chip 
                  label={getIntegrationStatus('outlook') === 'connected' ? 'Connected' : 'Disconnected'}
                  color={getIntegrationStatus('outlook') === 'connected' ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your Outlook calendar to manage appointments and schedule calls.
              </Typography>
              {getIntegrationStatus('outlook') === 'connected' ? (
                <Button 
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => handleRevoke(integrations.find(i => i.provider === 'outlook').id)}
                >
                  Disconnect Outlook
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  startIcon={<LinkIcon />}
                  onClick={handleOutlookConnect}
                >
                  Connect Outlook
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Google Calendar */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Google Calendar</Typography>
                <Chip 
                  label={getIntegrationStatus('google_calendar') === 'connected' ? 'Connected' : 'Disconnected'}
                  color={getIntegrationStatus('google_calendar') === 'connected' ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your Google Calendar to manage appointments and schedule calls.
              </Typography>
              {getIntegrationStatus('google_calendar') === 'connected' ? (
                <Button 
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => handleRevoke(integrations.find(i => i.provider === 'google_calendar').id)}
                >
                  Disconnect Google Calendar
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  startIcon={<LinkIcon />}
                  onClick={handleGoogleConnect}
                >
                  Connect Google Calendar
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Go High Level */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">LeadConnector</Typography>
                <Chip 
                  label={getIntegrationStatus('ghl') === 'connected' ? 'Connected' : 'Disconnected'}
                  color={getIntegrationStatus('ghl') === 'connected' ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your LeadCnnector sync leads and automate messaging.
              </Typography>
              {getIntegrationStatus('ghl') === 'connected' ? (
                <Button 
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => handleRevoke(integrations.find(i => i.provider === 'ghl').id)}
                >
                  Disconnect GHL
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  startIcon={<LinkIcon />}
                  onClick={handleGhlConnect}
                >
                  LeadCnnector
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Integrations List */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active Integrations
          </Typography>
          {integrations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No active integrations found.
            </Typography>
          ) : (
            <List>
              {integrations.map((integration, index) => (
                <React.Fragment key={integration.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" fontWeight="medium">
                            {integration.provider === 'outlook' ? 'Microsoft Outlook' : 
                             integration.provider === 'google_calendar' ? 'Google Calendar' : 
                             integration.provider === 'ghl' ? 'Go High Level' : integration.provider}
                          </Typography>
                          <Chip 
                            label="Active" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 2 }}
                          />
                        </Box>
                      }
                      secondary={`Connected on ${new Date(integration.createdAt).toLocaleDateString()}`}
                    />
                    <Button 
                      variant="outlined" 
                      color="error"
                      size="small"
                      onClick={() => handleRevoke(integration.id)}
                    >
                      Revoke Access
                    </Button>
                  </ListItem>
                  {index < integrations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default IntegrationsPage;